import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBriefcase, FiUser, FiGlobe, FiHome, FiChevronDown } = FiIcons;

const TOPMATE_URL = 'https://topmate.io/acharya_ji_ajay_l_agrawal/2021138?utm_source=public_profile&utm_campaign=acharya_ji_ajay_l_agrawal';

const profiles = [
  {
    Icon: FiBriefcase,
    label: 'Business Owners & Founders',
    sub: 'Founders and operators at decision crossroads',
    pain: [
      "You've built something real — but a key decision is stalling everything.",
      'The deal, the hire, the pivot. You keep going back and forth.',
      "Everyone around you has an opinion. None of it feels settled.",
    ],
    whisper: "You're not confused. You're waiting for clarity that logic alone can't provide.",
    color: 'from-amber-50 to-white',
    accent: 'border-amber-200',
    dot: 'bg-amber-400',
  },
  {
    Icon: FiUser,
    label: 'Executives & Professionals',
    sub: 'Navigating career transitions and high-stakes moves',
    pain: [
      "The promotion, the offer, the resignation letter — all drafted, none sent.",
      "You've been in this cycle for months and the pressure is building.",
      "Something keeps telling you the timing isn't right. But you can't explain why.",
    ],
    whisper: 'Career transitions are never just professional. They touch everything.',
    color: 'from-blue-50 to-white',
    accent: 'border-blue-200',
    dot: 'bg-blue-400',
  },
  {
    Icon: FiGlobe,
    label: 'NRIs & Global Families',
    sub: 'Clients across 15+ countries navigating cross-border decisions',
    pain: [
      "You're managing money, property, and family across two countries.",
      "The decisions compound. A wrong move here affects everything there.",
      "And you can't always explain what you're feeling to people around you.",
    ],
    whisper: "Distance doesn't simplify decisions. It amplifies them.",
    color: 'from-emerald-50 to-white',
    accent: 'border-emerald-200',
    dot: 'bg-emerald-400',
  },
  {
    Icon: FiHome,
    label: 'Families at Turning Points',
    sub: 'Long-term planning, property, and Vastu guidance',
    pain: [
      "A property purchase, a business succession, a family disagreement over direction.",
      "The stakes are high because it's not just your future — it's theirs too.",
      "You need someone who understands the weight of that responsibility.",
    ],
    whisper: "Some decisions carry the weight of generations. They deserve more than a spreadsheet.",
    color: 'from-rose-50 to-white',
    accent: 'border-rose-200',
    dot: 'bg-rose-400',
  },
];

function ProfileCard({ profile, index }) {
  const [open, setOpen] = useState(false);
  const { Icon } = profile;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.12, duration: 0.7 }}
      className={`rounded-sm border-2 ${profile.accent} bg-gradient-to-br ${profile.color} overflow-hidden group cursor-pointer shadow-md hover:shadow-lg transition-shadow`}
      onClick={() => setOpen(!open)}
    >
      <div className="p-8 flex items-start justify-between gap-6">
        <div className="flex items-start gap-5">
          <div className="w-14 h-14 rounded-full bg-white border-2 border-stone-100 flex items-center justify-center flex-shrink-0 shadow-sm mt-0.5">
            <Icon className="text-brand-gold text-2xl" />
          </div>
          <div>
            <p className="font-serif text-brand-dark text-2xl leading-snug mb-2">{profile.label}</p>
            <p className="text-sm text-stone-600 font-medium leading-relaxed">{profile.sub}</p>
          </div>
        </div>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }} className="flex-shrink-0 mt-2">
          <FiChevronDown className="text-stone-500 text-2xl" />
        </motion.div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-8 pb-8 space-y-6">
              <div className="w-full h-px bg-stone-200" />
              <ul className="space-y-4">
                {profile.pain.map((point, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08, duration: 0.4 }}
                    className="flex items-start gap-4"
                  >
                    <span className={`mt-2.5 w-2 h-2 rounded-full ${profile.dot} flex-shrink-0`} />
                    <span className="text-stone-700 font-medium text-lg leading-relaxed">{point}</span>
                  </motion.li>
                ))}
              </ul>
              <div className="border-l-4 border-brand-gold/50 pl-5 pt-2 bg-white/50 py-3 rounded-r-sm">
                <p className="text-brand-dark font-serif text-lg italic leading-relaxed">"{profile.whisper}"</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function TrustedBy() {
  return (
    <section className="py-28 px-6 md:px-12 bg-brand-accent">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center mb-10"
        >
          <p className="text-sm uppercase tracking-[0.3em] text-brand-gold font-bold mb-4">Who Reaches Out</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-dark mb-6 leading-tight">
            You Probably Recognize <br />
            <span className="italic text-brand-goldDark">Yourself Here</span>
          </h2>
          <p className="text-stone-700 font-medium text-xl max-w-2xl mx-auto leading-relaxed">
            People don't usually reach out because things are going badly. They reach out because something important is approaching — and the usual tools aren't enough.
          </p>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="text-center text-sm uppercase tracking-widest text-stone-500 font-bold mb-12"
        >
          Tap a profile to see if it resonates
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {profiles.map((profile, i) => (
            <ProfileCard key={i} profile={profile} index={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="bg-brand-dark rounded-sm p-10 md:p-12 flex flex-col md:flex-row items-center md:items-start gap-10 shadow-2xl"
        >
          <div className="flex-1">
            <p className="font-serif text-2xl md:text-3xl text-white italic leading-relaxed mb-4">
              "The decisions that weigh on you longest are usually the ones that deserve a deeper lens."
            </p>
            <p className="text-brand-gold text-sm uppercase tracking-widest font-bold">Acharyaji Ajay L Agarwal</p>
          </div>
          <div className="flex-shrink-0 w-full md:w-auto">
            <a
              href={TOPMATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full md:w-auto text-center px-10 py-5 bg-brand-gold text-brand-dark text-lg font-bold tracking-widest uppercase hover:bg-white transition-all duration-400 rounded-sm shadow-md"
            >
              Book a Conversation
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}